import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { NavBarElement } from "../../../pages/platform/components/nav-bar-element.enum"
import { useNavBar } from "../hooks/useNavBar"
import { AdminFeaturesDropdown } from "./AdminFeaturesDropdown"
import { EnvironmentIndicator } from "./EnvironmentIndicator"
import { AccountIcon, AnalyticsIcon, AppointmentsIcon, ChatIcon, CustomersIcon, ExpertsIcon, HomeIcon, MaterialIcon } from "./icons"

interface Props {
    currentItem: NavBarElement,
}

const NavBar: React.FC<Props> = ({
    currentItem,
}) => {
    const { 
        navItems, 
        role, 
        handleNavigation, 
        handleKeyPress, 
        isItemActive, 
        getChatNotificationCount, 
        hasChatNotifications 
    } = useNavBar(currentItem);

    // Icon configuration map for better maintainability
    const iconConfig: Record<NavBarElement, {
        component: React.ComponentType<any>;
        size?: number;
        className: string;
    } | null> = {
        [NavBarElement.Home]: {
            component: HomeIcon,
            size: 22,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Chat]: {
            component: ChatIcon,
            size: 21,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Experts]: {
            component: ExpertsIcon,
            size: 16,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Customers]: {
            component: CustomersIcon,
            size: 25,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Appointements]: {
            component: AppointmentsIcon,
            size: 20,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Material]: {
            component: MaterialIcon,
            size: 18,
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Account]: {
            component: AccountIcon,
            size: undefined, // AccountIcon doesn't use size prop
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.Analytics]: {
            component: AnalyticsIcon,
            size: undefined, // AnalyticsIcon doesn't use size prop
            className: "transition-transform duration-200 group-hover:scale-110"
        },
        [NavBarElement.AdminFeatures]: null // AdminFeatures uses dropdown, not icon
    };

    // Function to render the appropriate icon for each nav item
    const renderIcon = (element: NavBarElement, isActive: boolean) => {
        const config = iconConfig[element];
        
        if (config) {
            const IconComponent = config.component;
            const props = {
                isActive,
                className: config.className,
                ...(config.size && { size: config.size })
            };
            
            return <IconComponent {...props} />;
        }
        
        // Fallback for any unmapped elements
        return (
            <div className="w-6 h-6 bg-gray-400 rounded flex items-center justify-center">
                <span className="text-white text-xs">?</span>
            </div>
        );
    };

    return (
        <nav 
            className="w-full h-[90px] md:w-[100px] md:h-full flex items-center justify-center bg-holifya-navy-blue shadow-lg"
            role="navigation"
            aria-label="Main navigation"
        >
            <div className="w-full h-[90px] md:h-full flex md:flex-col justify-center md:items-center px-4 md:px-2 md:py-56 relative">
                {/* Environment indicator */}
                <EnvironmentIndicator 
                    role={role} 
                    className="absolute bottom-2 left-4 md:static md:bottom-auto md:left-auto"
                />
                
                {/* Navigation items */}
                <div className="flex md:flex-col items-center justify-center w-full h-full min-w-fit md:min-w-0 md:space-y-9">
                    {navItems.map(({ element, title, path, description }) => {
                        const isActive = isItemActive(element);
                        
                        // Special handling for AdminFeatures dropdown
                        if (element === NavBarElement.AdminFeatures) {
                            return (
                                <AdminFeaturesDropdown 
                                    key={element}
                                    isActive={isActive} 
                                    onItemClick={() => {}}
                                />
                            );
                        }
                        
                        return (
                            <NavBarSingleElement
                                key={element}
                                icon={renderIcon(element, isActive)}
                                title={title}
                                description={description}
                                isActive={isActive}
                                onClick={() => handleNavigation(path)}
                                onKeyPress={(e) => handleKeyPress(e, path)}
                                showNotification={element === NavBarElement.Chat && hasChatNotifications()}
                                notificationCount={getChatNotificationCount()}
                            />
                        );
                    })}
                </div>
            </div>
        </nav>
    )
}

export default NavBar

interface ElementProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    isActive: boolean;
    onClick: () => void;
    onKeyPress: (event: React.KeyboardEvent) => void;
    showNotification?: boolean;
    notificationCount?: number;
}

const NavBarSingleElement: React.FC<ElementProps> = ({
    icon,
    title,
    description,
    isActive,
    onClick,
    onKeyPress,
    showNotification = false,
    notificationCount = 0,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const elementRef = useRef<HTMLDivElement>(null);

    const updateTooltipPosition = () => {
        if (elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();
            setTooltipPosition({
                x: rect.right + 8, // 8px margin
                y: rect.top + rect.height / 2
            });
        }
    };

    const handleMouseEnter = () => {
        updateTooltipPosition();
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    // Update position on window resize
    useEffect(() => {
        const handleResize = () => {
            if (showTooltip) {
                updateTooltipPosition();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showTooltip]);

    return (
        <>
            <div
                ref={elementRef}
                className={`
                    relative group h-[90px] md:h-[48px] min-w-[60px] md:w-full flex flex-col items-center justify-center cursor-pointer
                    transition-all duration-200 ease-in-out
                    ${isActive ? 'scale-110' : 'hover:scale-105 active:scale-95'}
                    ${isActive ? 'opacity-100' : 'hover:opacity-90'}
                    touch-manipulation select-none
                    md:hover:scale-105 md:active:scale-95
                `}
                onClick={onClick}
                onKeyPress={onKeyPress}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                tabIndex={0}
                role="button"
                aria-label={`${title} - ${description}`}
                aria-current={isActive ? 'page' : undefined}
            >
                
                {/* Icon container */}
                <div className="mb-[6px] md:mb-[10px] h-[22px] relative">
                    {icon}
                    
                    {/* Notification badge */}
                    {showNotification && (
                        <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-holifya-red rounded-full flex items-center justify-center shadow-md">
                            <span className="text-white text-xs font-bold">
                                {notificationCount > 99 ? '99+' : notificationCount}
                            </span>
                        </div>
                    )}
                </div>
                
                {/* Title */}
                <div className={`
                    font-bold text-[10px] md:text-[11px] leading-[14px] md:leading-[16px] text-white
                    transition-all duration-200 text-center
                    ${isActive ? 'text-white' : 'group-hover:text-gray-200'}
                `}>
                    {title}
                </div>
                
                {/* Mobile active indicator */}
                <div className={`
                    absolute top-0 left-1/2 transform -translate-x-1/2
                    w-1 h-1 bg-white rounded-full
                    transition-all duration-200
                    md:hidden
                    ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
                `} />
                
                {/* Mobile touch feedback overlay */}
                <div className="
                    absolute inset-0 bg-white opacity-0 
                    transition-opacity duration-150
                    active:opacity-10
                    md:hidden
                    rounded-lg
                " />
            </div>

            {/* Portal-based tooltip for desktop */}
            {showTooltip && typeof window !== 'undefined' && createPortal(
                <div 
                    className="
                        fixed bg-gray-800 text-white text-sm px-3 py-2 rounded-lg
                        pointer-events-none whitespace-nowrap z-[99999]
                        shadow-xl border border-gray-700
                        transition-opacity duration-200
                        hidden md:block
                    "
                    style={{
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                        transform: 'translateY(-50%)'
                    }}
                >
                    <div className="font-semibold">{title}</div>
                    <div className="text-xs text-gray-300">{description}</div>
                    {/* Tooltip arrow */}
                    <div 
                        className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-0 border-r-4 border-y-2 border-transparent border-r-gray-800" 
                    />
                </div>,
                document.body
            )}
        </>
    )
}
